import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { PATIENT_SECTIONS, PATIENT_EMPTY, patientPayload } from "@/lib/patientFields";

/** Shared create/edit dialog for the complete patient record. */
export const PatientFormDialog = ({ open, onOpenChange, patient, facilityId, onSaved }) => {
  const editing = Boolean(patient?.id);
  const [form, setForm] = useState(() => ({ ...PATIENT_EMPTY, ...(patient || {}) }));
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    if (open) setForm({ ...PATIENT_EMPTY, ...(patient || {}) });
  }, [open, patient]);

  const save = async () => {
    if (!String(form.name || "").trim()) return toast.error("Patient name is required");
    const target = patient?.facility_id || facilityId;
    if (!target) return toast.error("Select a facility first");
    setSaving(true);
    try {
      const payload = patientPayload(form, target);
      const { data } = editing
        ? await api.put(`/patients/${patient.id}`, payload)
        : await api.post("/patients", payload);
      toast.success(editing ? "Patient record updated" : `Patient registered — ${data.uhid}`);
      onOpenChange(false);
      onSaved?.(data);
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="patient-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">
            {editing ? `Edit patient — ${patient.uhid}` : "Patient registration"}
          </DialogTitle>
        </DialogHeader>
        <Tabs defaultValue={PATIENT_SECTIONS[0].id}>
          <TabsList>
            {PATIENT_SECTIONS.map((s) => (
              <TabsTrigger key={s.id} value={s.id} data-testid={`tab-${s.id}`}>{s.label}</TabsTrigger>
            ))}
          </TabsList>
          {PATIENT_SECTIONS.map((s) => (
            <TabsContent key={s.id} value={s.id}
              className={`grid gap-4 pt-4 ${s.cols === 3 ? "sm:grid-cols-3" : "sm:grid-cols-2"}`}>
              {s.fields.map(([key, label, kind]) => (
                <div key={key} className="space-y-1.5">
                  <Label className="text-xs">{label}</Label>
                  {kind === "area" ? (
                    <Textarea rows={2} value={form[key] ?? ""} data-testid={`patient-${key}-input`}
                      onChange={(e) => setForm({ ...form, [key]: e.target.value })} />
                  ) : (
                    <Input type={kind || "text"} value={form[key] ?? ""} data-testid={`patient-${key}-input`}
                      onChange={(e) => setForm({ ...form, [key]: e.target.value })} />
                  )}
                </div>
              ))}
            </TabsContent>
          ))}
        </Tabs>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="save-patient-button">
            {editing ? "Save changes" : "Save patient"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
